import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useDeskHabit } from '@/hooks/useDeskHabit';
import { celebrateGraduation, celebrateStep } from '@/lib/celebration';
import { graduationCopy } from '@/lib/deskCelebrationCopy';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';

export const FEATURE_META: FeatureMeta = {
  id: 'desk-setup-celebration',
  label: 'Desk Setup celebration',
  description: 'Confetti when a Desk Setup step is finished, and a welcome to Your Next Move™ when all steps are done.',
  triggerType: 'dialog',
  personas: ['seller', 'buyer_seller', 'buyer'],
};

const seenKey = (uid: string) => `cfx-desk-celebrated:${uid}`;
interface Seen { steps: string[]; graduated: boolean }
function readSeen(uid: string): Seen | null {
  try { return JSON.parse(localStorage.getItem(seenKey(uid)) || 'null'); } catch { return null; }
}
function writeSeen(uid: string, s: Seen) { try { localStorage.setItem(seenKey(uid), JSON.stringify(s)); } catch { /* private mode */ } }

/** Watches Desk Setup progress on every signed-in page; celebrates each newly finished step once, and graduation once. */
export default function DeskCelebrations() {
  useFeatureRegistration(FEATURE_META);
  const { user } = useAuth();
  const { progress, reps, persona } = useDeskHabit();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    if (!user || !progress) return;
    const done = reps.filter((r) => r.done).map((r) => r.key);
    const prev = readSeen(user.id);
    // First sighting (or already graduated before this existed): record silently, never replay history.
    if (!prev) { writeSeen(user.id, { steps: done, graduated: progress.graduated }); return; }
    const fresh = done.filter((k) => !prev.steps.includes(k));
    const justGraduated = progress.graduated && !prev.graduated;
    writeSeen(user.id, { steps: Array.from(new Set([...prev.steps, ...done])), graduated: prev.graduated || progress.graduated });
    if (!progress.celebrationsEnabled || fired.current) return;
    if (justGraduated) { fired.current = true; setOpen(true); celebrateGraduation(); }
    else if (fresh.length) celebrateStep();
  }, [user, progress, reps]);

  if (!open) return null;
  const copy = graduationCopy(persona);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg text-center px-8 py-10">
        <p className="text-4xl" aria-hidden>🎉</p>
        <DialogTitle className="font-display text-3xl mt-2">Congratulations, you’re all set up.</DialogTitle>
        <DialogDescription className="text-base mt-3">{copy.lead}</DialogDescription>
        <p className="mt-3 text-sm text-muted-foreground">{copy.nextMove}</p>
        <Button className="mt-6 w-full sm:w-auto mx-auto" onClick={() => { setOpen(false); navigate('/desk'); }}>
          See Your Next Move™
        </Button>
      </DialogContent>
    </Dialog>
  );
}
